import { PageShell, GetStarted } from '../../components';
import { PageBody, PageHero, Section, FeatureRows, DataTable } from '../../components/page';

/**
 * Comparison: Statio against building the same layer in-house. Claims are
 * checked against .impeccable/product-truth.md; positioning follows Pugio's
 * docs/POSITIONING.md (lead with the per-tool problem, never "platform").
 */

const PROBLEM = [
  {
    title: 'Most of it is plumbing',
    body: 'Encrypted credential storage, an auth layer, a logging pipeline, rate limits and tenant boundaries. Each is well understood, and each takes time to build, test and keep running.',
  },
  {
    title: 'One part is easy to get wrong',
    body: 'A single MCP server often holds a read tool and a destructive tool. Access set per server gives an agent both or neither. Statio grants a server to a member or an agent and lets you deny individual tools on top, checked at the gateway on every call.',
  },
];

const ROWS = [
  ['Time to implement', 'Months', 'Minutes to set up'],
  ['Encrypted credential storage', 'Build from scratch', 'Built-in, AES-256-GCM'],
  ['Access policies', 'Custom auth layer', 'Per-tool, for members and agents'],
  ['Audit trail', 'Custom logging pipeline', 'Automatic, every call'],
  ['Multi-tenancy', 'Complex to build correctly', 'Included'],
  ['Rate limiting', 'Build and tune yourself', 'Per organization, by plan'],
  ['Token lifetime', 'Build rotation logic', 'Access and agent tokens 5 min, refresh 7 days'],
  ['Maintenance', 'Your team, forever', 'Managed for you'],
];

const List = ({ items }: { items: string[] }) => (
  <>
    {items.map((t) => (
      <p key={t}>{t}</p>
    ))}
  </>
);

const FIT = [
  {
    title: 'When to build it yourself',
    body: (
      <List
        items={[
          'Your security requirements are specific enough that no vendor meets them.',
          'You have a dedicated security engineering team with bandwidth.',
          'You need to run entirely on-premises with zero external dependencies.',
          'Timeline is not a constraint. You have months to build, test, and harden.',
        ]}
      />
    ),
  },
  {
    title: 'When to choose Statio',
    body: (
      <List
        items={[
          'You want to ship this week, not in 6 months.',
          'Your team has better things to build than encrypted credential storage.',
          'You need audit trails and access policies for compliance, now.',
          'You want credential storage that’s already been hardened, not one you’re building for the first time.',
          'Your agents are already calling APIs and you need to secure them yesterday.',
        ]}
      />
    ),
  },
];

export default function StatioVsDiy() {
  return (
    <PageShell>
      <PageBody>
        <PageHero
          title="Statio compared with building it yourself"
          lede="Encrypted credential storage, access control, audit trails, multi-tenancy and rate limiting: you can build all of this, and the question is whether you should."
          art="cascade"
        />
        <Section id="building" title="What you would be building" split>
          <FeatureRows items={PROBLEM} columns={1} />
        </Section>
        <Section id="compare" title="Side by side">
          <DataTable
            caption="Statio compared with building it yourself"
            columns={['', 'Build it yourself', 'Statio']}
            rows={ROWS}
            highlight={2}
          />
        </Section>
        <Section id="fit" title="Which fits">
          <FeatureRows items={FIT} />
        </Section>
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}
